import { Alert, Box, Checkbox, Group, MultiSelect, Text } from '@mantine/core';
import type { AdPlatform } from '@marketing-tool/shared';
import { useMemo } from 'react';

import { useCampaigns } from '../../data/game-report';
import * as remote from '../../data/remote-data';

export const CampaignFilterSelector = ({
    id,
    gameId,
    platform,
    value,
    onChange,
    label,
    clearable,
}: {
    id: string;
    gameId: string;
    platform: AdPlatform;
    value: string[]; // Selected campaign names
    onChange: (campaignNames: string[]) => void;
    label?: string;
    clearable?: boolean;
}) => {
    const campaigns = useCampaigns(gameId, platform);

    const data = useMemo(
        () =>
            remote.map(campaigns.data, (campaigns) =>
                campaigns.map((campaign) => ({
                    value: campaign.name,
                    label: campaign.name,
                })),
            ),
        [campaigns.data],
    );

    return remote.matchAll(data, {
        NotAsked: () => null,
        Loading: () => (
            <Text c="dimmed" fs="italic">
                Loading campaigns...
            </Text>
        ),
        Failure: (error) => (
            <Alert color="red" title="Error loading campaigns">
                {error}
            </Alert>
        ),
        Success: (data) => {
            const allSelected = value.length === data.length && data.length > 0;
            return (
                <Box>
                    <MultiSelect
                        id={id}
                        label={label}
                        value={value}
                        onChange={onChange}
                        data={data}
                        placeholder="All campaigns included"
                        searchable
                        clearable={clearable}
                        maxDropdownHeight={400}
                        renderOption={({ option, checked }) => (
                            <Group gap="xs">
                                <Checkbox checked={checked} readOnly tabIndex={-1} size="xs" />
                                {option.label}
                            </Group>
                        )}
                        comboboxProps={{
                            withinPortal: true,
                            shadow: 'sm',
                        }}
                    />
                    {data.length > 0 && (
                        <Box mt={4}>
                            <Checkbox
                                size="xs"
                                label={allSelected ? 'Deselect All' : 'Select All'}
                                checked={allSelected}
                                indeterminate={value.length > 0 && !allSelected}
                                onChange={() => {
                                    if (allSelected) {
                                        onChange([]);
                                    } else {
                                        onChange(data.map((opt) => opt.value));
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            );
        },
    });
};
